/**
 * The two reads the workout screens make, over T18/T19's query options.
 *
 * - `useTodaysSession(requested?)`: the plan (`GET /me/plan`) and the recent
 *   history (`GET /me/sessions`, plus the session on this device, which may
 *   not have synced), run through the pure `chooseSession` rule — today's
 *   session, or the one the readiness check was opened for (`day=`).
 * - `usePreviousSetsSync()`: "last time" per exercise of the running session
 *   (`GET /me/exercise-history`), written into the store with `setPrevious`
 *   so the suggestions and the log view read one copy (T53).
 *
 * Both work offline from the persisted Query cache; neither blocks a
 * running session, which renders from the store's own copy of its items.
 */

import { activePlanOptions } from "@repo/client/data/plan";
import {
  exerciseHistoryOptions,
  sessionsOptions,
  workoutKeys,
} from "@repo/client/data/workout";
import { toCalendarDay } from "@repo/client/format";
import type {
  ExerciseRecordsOutput,
  PlanSessionView,
  PlanView,
} from "@repo/contract/training";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useApi } from "../../../data/api";
import { unavailable, useDataQuery } from "../../../data/query-client";
import {
  coveringHistory,
  coveringSessions,
} from "../../../data/sessions-placeholder";
import { appLocale } from "../../../lib/locale";
import { useWorkoutActions, useWorkoutSession } from "../store";
import {
  type RecordFloor,
  recordFloorsOf,
  withAllTimeRecords,
} from "../store/live-records";
import { previousKey, previousSetsByExercise } from "./previous-sets";
import {
  chooseSession,
  rotationHistoryOf,
  type TodaysSessionReason,
  type TrainedSession,
} from "./todays-session";
import { useDeviceSemantics } from "./use-device-semantics";

const DAY_MS = 86_400_000;
/** Enough history for a rotation to know the last workout of any sane week. */
const HISTORY_DAYS = 42;
/** "Last time" looks back this far; older is not last time any more. */
const PREVIOUS_DAYS = 180;

function daysAgo(days: number): string {
  return toCalendarDay(new Date(Date.now() - days * DAY_MS));
}

function catalogueLocale(): "pt" | "en" {
  return appLocale().startsWith("pt") ? "pt" : "en";
}

/** The hero's history window's `from` — Today's mood line (`D250`) reads
 *  the same key, so it costs no second request. */
export function recentHistoryFrom(): string {
  return daysAgo(HISTORY_DAYS);
}

export type TodaysSessionState =
  | { readonly status: "loading" }
  | { readonly status: "error"; readonly retry: () => void }
  | { readonly status: "none"; readonly plan: PlanView | null }
  | {
      readonly plan: PlanView;
      readonly reason: TodaysSessionReason;
      readonly session: PlanSessionView;
      readonly status: "ready";
    };

/**
 * The last `HISTORY_DAYS` of bouts (`GET /me/sessions`), under the one key
 * Today's choice, the plan's deload days (`D248`) and the workout's header
 * all read — one request, one cache entry, offline from the persisted copy.
 */
export function useRecentHistory() {
  const api = useApi();
  const from = daysAgo(HISTORY_DAYS);
  const client = useQueryClient();
  // The range moves at midnight; yesterday's cached read covers today's
  // (`D190`), so the first open of the day draws the hero, not its skeleton.
  return useDataQuery(sessionsOptions(api, { from }), {
    placeholderData: () => coveringSessions(client, { from }),
  });
}

/**
 * Today's session. `requested` is a plan session id the person chose by hand
 * (the one-shot `day=`); it wins when the plan holds it, and every other
 * caller passes nothing and gets today's rule.
 */
export function useTodaysSession(requested?: string): TodaysSessionState {
  const api = useApi();
  const local = useWorkoutSession();
  const plan = useDataQuery(
    activePlanOptions(api, { locale: catalogueLocale() })
  );
  const history = useRecentHistory();

  // No `abandoned` here: the device's copy cannot know that another device
  // or the server's auto-close ended it as abandoned (`D177`). Its id lets
  // `historyWith` defer to the list, which does know.
  const localEntry: TrainedSession | null =
    local === null
      ? null
      : {
          planSessionId: local.planSessionId,
          sessionId: local.sessionId,
          startedAt: local.startedAt,
        };
  const trained = history.data?.sessions;

  return useMemo((): TodaysSessionState => {
    if (plan.data === undefined) {
      if (unavailable(plan)) {
        return {
          retry: () => {
            plan.refetch().catch(() => undefined);
            history.refetch().catch(() => undefined);
          },
          status: "error",
        };
      }
      return { status: "loading" };
    }
    // The history only sharpens the choice; without it (offline, first
    // load, an error) the rule still answers from the plan and the device.
    if (trained === undefined && !unavailable(history) && history.isPending) {
      return { status: "loading" };
    }
    if (plan.data === null) {
      return { plan: null, status: "none" };
    }
    const choice = chooseSession(
      {
        history: rotationHistoryOf(trained ?? [], localEntry),
        now: new Date(),
        sessions: plan.data.sessions,
      },
      requested
    );
    if (choice === null || choice.session.items.length === 0) {
      return { plan: plan.data, status: "none" };
    }
    return {
      plan: plan.data,
      reason: choice.reason,
      session: choice.session,
      status: "ready",
    };
  }, [plan, history, trained, localEntry, requested]);
}

/**
 * Feeds the running session's "last time" into the store. The exercise ids
 * are today's items and its swaps, all of them: `fetchExerciseHistory` splits
 * a list longer than the route's limit into requests of 20 (T30), so a long
 * session is not missing its "last time" past the twentieth exercise.
 */
export function usePreviousSetsSync(): void {
  const api = useApi();
  const client = useQueryClient();
  const actions = useWorkoutActions();
  const session = useWorkoutSession();
  const running = session !== null && session.ended === null;
  const sessionId = session?.sessionId ?? "";
  const items = session?.items;
  const swaps = session?.swaps;
  const exerciseIds = useMemo(() => {
    const ids = new Set<string>();
    for (const item of items ?? []) {
      ids.add(item.exerciseId);
    }
    for (const swap of Object.values(swaps ?? {})) {
      ids.add(swap.exerciseId);
    }
    return [...ids];
  }, [items, swaps]);
  const from = daysAgo(PREVIOUS_DAYS);
  const history = useDataQuery(
    exerciseHistoryOptions(api, { exerciseIds, from }),
    {
      enabled: running && exerciseIds.length > 0,
      // The day moved since the last read: draw from the one that covers it.
      placeholderData: () => coveringHistory(client, { exerciseIds, from }),
    }
  );
  const stored = session?.previous;
  const storedRecords = session?.records;
  // The live record's bar (`D243`): each exercise's records before today,
  // read through the same load semantics the alert judges with.
  const semantics = useDeviceSemantics(session);

  useEffect(() => {
    if (!running || history.data === undefined || stored === undefined) {
      return;
    }
    const previous = previousSetsByExercise(history.data.sets, sessionId);
    const records = withCachedRecords(
      recordFloorsOf(history.data.sets, sessionId, semantics),
      exerciseIds,
      sessionId,
      (exerciseId) =>
        client.getQueryData<ExerciseRecordsOutput>(
          workoutKeys.records(exerciseId)
        )
    );
    if (
      previousKey(previous) !== previousKey(stored) ||
      JSON.stringify(records) !== JSON.stringify(storedRecords ?? {})
    ) {
      actions.setPrevious(previous, records);
    }
  }, [
    running,
    history.data,
    stored,
    storedRecords,
    sessionId,
    actions,
    semantics,
    exerciseIds,
    client,
  ]);
}

/**
 * The history's floors raised by the all-time records the exercise sheet
 * left in the Query cache (`D243`): a record older than the history's 180
 * days still counts, offline included. Nothing is fetched for this — an
 * exercise whose sheet was never opened reads the history alone.
 */
function withCachedRecords(
  floors: Record<string, RecordFloor>,
  exerciseIds: readonly string[],
  sessionId: string,
  cached: (exerciseId: string) => ExerciseRecordsOutput | undefined
): Record<string, RecordFloor> {
  const out: Record<string, RecordFloor> = { ...floors };
  for (const exerciseId of exerciseIds) {
    const output = cached(exerciseId);
    const raised =
      output === undefined
        ? out[exerciseId]
        : withAllTimeRecords(out[exerciseId], output, sessionId);
    if (raised !== undefined) {
      out[exerciseId] = raised;
    }
  }
  return out;
}
